import { Button, Image, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import LicenseCard from './LicenseCard';
// import UserHeader from '../UserAccount/UserHeader';
import Header from '../../components/MarginalsV2/Header';
import Footer from '../../components/MarginalsV2/Footer';
import IDCaptureOverlay from '../../components/IDCaptureOverlay';
import useWidget from '../../hooks/useWidget';
import React, { useEffect, useRef, useState } from 'react';

export default function ScanLicense() {
  const {
    isUploadFront,
    setIsUploadFront,
    isUploadBack,
    setIsUploadBack,
    widget1,
    widget2,
    updateDocumentImage,
    uploadFolder,
  } = useWidget();

  const [captureOpen, setCaptureOpen] = useState(false);
  const [captureSide, setCaptureSide] = useState(null);
  const captureSideRef = useRef(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const matcher = window.matchMedia('(max-width: 767px)');
    const handleResize = (event) => setIsMobile(event.matches);
    setIsMobile(matcher.matches);
    if (matcher.addEventListener) {
      matcher.addEventListener('change', handleResize);
    } else {
      matcher.addListener(handleResize);
    }
    return () => {
      if (matcher.removeEventListener) {
        matcher.removeEventListener('change', handleResize);
      } else {
        matcher.removeListener(handleResize);
      }
    };
  }, []);

  const { t } = useTranslation();

  const uploadCapturedImage = async (dataUrl, side, metrics) => {
    if (!dataUrl || !side) return;

    const formData = new FormData();
    formData.append('file', dataUrl);
    formData.append('upload_preset', 'fz7jch0p');
    formData.append('folder', uploadFolder);

    if (metrics) {
      const { edge, focus, reflection, coverage, brightness } = metrics;
      formData.append('context', `debug_edge=${edge}|debug_focus=${focus}|debug_reflection=${reflection}|debug_coverage=${coverage}|debug_brightness=${brightness}`);
      formData.append('tags', `debug,debug_edge_${edge},debug_focus_${focus},debug_reflection_${reflection},debug_coverage_${coverage},debug_brightness_${brightness}`);
    }

    setIsUploadingImage(true);

    try {
      const response = await fetch('https://api.cloudinary.com/v1_1/tidp/image/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Cloudinary upload failed');
      }

      const uploadResult = await response.json();
      const secureUrl = uploadResult.secure_url;

      if (side === 'front') {
        setIsUploadFront(true);
        document.getElementById('imagefront')?.setAttribute('src', secureUrl);
        await updateDocumentImage('nationalLicenseAttributes.frontImageUrl', secureUrl);
      } else if (side === 'back') {
        setIsUploadBack(true);
        document.getElementById('imageback')?.setAttribute('src', secureUrl);
        await updateDocumentImage('nationalLicenseAttributes.backImageUrl', secureUrl);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsUploadingImage(false);
      setCaptureOpen(false);
      setCaptureSide(null);
    }
  };

  const openCloudinaryWidget = (widget) => {
    setCaptureOpen(false);
    setCaptureSide(null);
    // don't clear captureSideRef.current here — the caller should pass the
    // intended side and the widget callbacks rely on it to determine
    // whether this is a front/back upload. Clear the ref later if needed.
    widget?.open();
  };

  const handleMobileCapture = (side) => {
    setCaptureSide(side);
    captureSideRef.current = side;
    setCaptureOpen(true);
  };
  const navigate = useNavigate();
  const { lang } = useParams();

  useEffect(() => {
    document.title = t("license_title")
  }, []);

  return (
    <div>
      <Header renderBg={true} className="py-2" />
      <h1 className='mt-4 mb-3'>{t('license_title')}</h1>
      <div className='d-flex flex-column align-items-center column-gap'>
        <div className='mb-4 mt-3 border-dashed'>
          {isUploadFront ? (
            <>
              <Image id='imagefront' src='' className='thumbnail' />
              <div className='d-flex justify-content-center mt-2'>
                <p className='mx-5'>FRONT PHOTO</p>
                <OverlayTrigger
                  delay={{ hide: 300, show: 300 }}
                  overlay={(props) => (
                    <Tooltip {...props}>Try another photo</Tooltip>
                  )}
                  placement='right'
                >
                  <Icon
                    icon='akar-icons:trash-can'
                    width='35'
                    height='35'
                    color='#39c0ed'
                    onClick={() => setIsUploadFront(false)}
                  />
                </OverlayTrigger>
              </div>
            </>
          ) : (
            <LicenseCard
              text={
                <>
                  {t('license_text')} <br />({t('license_front')})
                </>
              }
              // icon={'fa:drivers-license-o'}
              image={'/assets/images/front.jpg'}
              widget={widget1}
              onClick={isMobile ? () => handleMobileCapture('front') : undefined}
            />
          )}
        </div>
        <div className='mb-5 mt-5 border-dashed'>
          {isUploadBack ? (
            <>
              <Image id='imageback' src='' className='thumbnail' />
              <div className='d-flex justify-content-center mt-2'>
                <p className='mx-5'>BACK PHOTO</p>
                <OverlayTrigger
                  delay={{ hide: 300, show: 300 }}
                  overlay={(props) => (
                    <Tooltip {...props}>Try another photo</Tooltip>
                  )}
                  placement='right'
                >
                  <Icon
                    icon='akar-icons:trash-can'
                    width='35'
                    height='35'
                    color='#39c0ed'
                    onClick={() => setIsUploadBack(false)}
                  />
                </OverlayTrigger>
              </div>
            </>
          ) : (
            <LicenseCard
              text={
                <>
                  {t('license_text')} <br />({t('license_back')})
                </>
              }
              // icon={'bi:card-list'}
              image={'/assets/images/back.jpg'}
              widget={widget2}
              onClick={isMobile ? () => handleMobileCapture('back') : undefined}
            />
          )}
        </div>
      </div>
      <div className='d-flex flex-column align-items-center px-5 my-4'>
        <p className='h3'>{t('license_confirm')}</p>
        <Button
          variant='info'
          className='btn-white mt-3 mb-5'
          size='lg'
          onClick={() => navigate(`/${lang}/checkout`)}
        >
          {t('license_button')}
        </Button>
      </div>
      <IDCaptureOverlay
        key={captureSide || 'idle'}
        open={captureOpen}
        isUploading={isUploadingImage}
        titleText={
          captureSide === 'front'
            ? `${t('id_capture_header_title')} - ${t('license_front')}`
            : captureSide === 'back'
            ? `${t('id_capture_header_title')} - ${t('license_back')}`
            : t('id_capture_header_title')
        }
        onClose={() => {
          setCaptureOpen(false);
          setCaptureSide(null);
          captureSideRef.current = null;
        }}
        onOpenGallery={() => openCloudinaryWidget(captureSide === 'front' ? widget1 : widget2)}
        onCaptureComplete={(dataUrl, metrics) => uploadCapturedImage(dataUrl, captureSide, metrics)}
      />
      <Footer />
    </div>
  );
}
